import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X, HardHat, Loader2, ClipboardList, StickyNote, Link2, LifeBuoy, CalendarDays, CalendarRange, Package } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-sky-50 text-sky-800 border-sky-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const STATUS_LABEL = { pending: "Da fare", in_progress: "In corso", completed: "Completata" };

function fmtDate(d) {
  if (!d) return "";
  return new Date(`${d}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
}

export default function InstallerAttivita({ reloadKey, onNew, onEdit, onOpenLinked }) {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setQ(qInput), 300);
    return () => clearTimeout(h);
  }, [qInput]);

  const load = useCallback(async () => {
    try {
      const params = { task_type: "activity" };
      if (statusFilter) params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/installers/tasks", { params });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento attività");
      setRows([]);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load, reloadKey]);

  async function changeStatus(t, status) {
    try {
      await api.patch(`/installers/tasks/${t.id}/status`, { status });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore cambio stato");
    }
  }

  async function deleteActivity(t) {
    if (!window.confirm(`Eliminare l'attività "${t.title}"?`)) return;
    try {
      await api.delete(`/installers/tasks/${t.id}`);
      toast.success("Attività eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  return (
    <div className="space-y-4" data-testid="installers-attivita">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Cerca attività (istruzioni, note…)"
            className="w-full border border-ink/20 pl-9 pr-9 py-2.5 rounded-sm bg-white text-sm"
            data-testid="attivita-search"
          />
          {qInput && (
            <button onClick={() => setQInput("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-ink" data-testid="attivita-search-clear"><X size={14} /></button>
          )}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="attivita-status-filter">
          <option value="active">Da fare + in corso</option>
          <option value="">Tutte (incl. completate)</option>
          <option value="pending">Da fare</option>
          <option value="in_progress">In corso</option>
          <option value="completed">Completate</option>
        </select>
        <button onClick={onNew} className="ml-auto inline-flex items-center gap-2 bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="attivita-new-btn">
          <Plus size={14} /> Nuova attività
        </button>
      </div>

      {!rows ? (
        <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="attivita-empty">
          Nessuna attività{statusFilter || q ? " con questi filtri" : ": crea la prima con «Nuova attività» (es. carico merce il giorno prima di una consegna)"}.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} className="bg-white border border-ink/10 border-l-4 border-l-amber-500 rounded-sm p-4 flex items-start gap-4 flex-wrap" data-testid={`attivita-card-${t.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <ClipboardList size={14} className="text-amber-600 shrink-0" />
                  <span className="font-medium text-ink">{t.title}</span>
                  <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${STATUS_STYLE[t.status]}`} data-testid={`attivita-status-badge-${t.id}`}>{STATUS_LABEL[t.status]}</span>
                </div>
                <div className="text-xs text-slate1 mt-1.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 font-medium text-ink capitalize"><CalendarDays size={11} /> {fmtDate(t.scheduled_date) || "Senza data"}{t.scheduled_time ? ` · ${t.scheduled_time}` : ""}</span>
                  {t.scheduled_end_date && t.scheduled_end_date > t.scheduled_date && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid={`attivita-span-${t.id}`}><CalendarRange size={11} /> fino al {fmtDate(t.scheduled_end_date)}</span>
                  )}
                  <span className="inline-flex items-center gap-1" data-testid={`attivita-workers-${t.id}`}><HardHat size={11} /> {(t.workers || []).map((w) => w.name).join(", ") || "Non assegnata"}</span>
                  {(t.goods || []).length > 0 && (
                    <span className="inline-flex items-center gap-1" data-testid={`attivita-goods-${t.id}`}><Package size={11} /> {(t.goods || []).filter((g) => g.loaded).length}/{(t.goods || []).length} a magazzino</span>
                  )}
                </div>
                {t.notes_admin && (
                  <div className="text-[11px] text-amber-900 mt-1.5 inline-flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1" data-testid={`attivita-notes-${t.id}`}>
                    <StickyNote size={11} className="shrink-0 mt-0.5 text-amber-600" /> {t.notes_admin}
                  </div>
                )}
                {t.linked_task && (
                  <div className="mt-1.5">
                    <button
                      onClick={() => onOpenLinked(t.linked_task.id)}
                      className="text-[11px] text-sky-900 inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 rounded-sm px-2 py-1 hover:bg-sky-100"
                      data-testid={`attivita-linked-${t.id}`}
                      title="Apri la consegna/assistenza collegata"
                    >
                      {t.linked_task.task_type === "assistance" ? <LifeBuoy size={11} className="text-violet-600" /> : <Link2 size={11} className="text-sky-600" />}
                      {t.linked_task.task_type === "assistance" ? "Assistenza" : "Consegna"} collegata: <b>{t.linked_task.client_name || t.linked_task.title}</b>
                      {t.linked_task.scheduled_date ? ` · ${fmtDate(t.linked_task.scheduled_date)}` : ""}
                      {t.linked_task.client_address ? ` · ${t.linked_task.client_address}` : ""}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} className="border border-ink/15 px-2 py-1.5 rounded-sm bg-cream text-xs" data-testid={`attivita-status-${t.id}`}>
                  <option value="pending">Da fare</option>
                  <option value="in_progress">In corso</option>
                  <option value="completed">Completata</option>
                </select>
                <button onClick={() => onEdit(t)} className="p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica" data-testid={`attivita-edit-${t.id}`}><Pencil size={15} /></button>
                <button onClick={() => deleteActivity(t)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina" data-testid={`attivita-del-${t.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ActivityModal({ task, workers, defaultDate, onClose, onSaved }) {
  const editing = !!task;
  const [form, setForm] = useState(() => task ? {
    title: task.title || "",
    notes_admin: task.notes_admin || "",
    scheduled_date: task.scheduled_date || new Date().toLocaleDateString("sv-SE"),
    scheduled_end_date: task.scheduled_end_date || "",
    scheduled_time: task.scheduled_time || "",
    assigned_to_ids: task.assigned_to_ids || [],
    goods: (task.goods || []).map((g) => ({ ...g })),
  } : {
    title: "", notes_admin: "",
    scheduled_date: defaultDate || new Date().toLocaleDateString("sv-SE"),
    scheduled_end_date: "",
    scheduled_time: "", assigned_to_ids: [], goods: [],
  });
  const [linked, setLinked] = useState(task?.linked_task || null);
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (linkQ.trim().length < 2) { setLinkResults([]); return undefined; }
    const h = setTimeout(async () => {
      setSearching(true);
      try {
        const [d1, d2] = await Promise.all([
          api.get("/installers/tasks", { params: { q: linkQ.trim(), task_type: "delivery" } }),
          api.get("/installers/tasks", { params: { q: linkQ.trim(), task_type: "assistance" } }),
        ]);
        const seen = new Set();
        setLinkResults([...d1.data, ...d2.data].filter((t) => !seen.has(t.id) && seen.add(t.id)).slice(0, 8));
      } catch {
        setLinkResults([]);
      }
      setSearching(false);
    }, 350);
    return () => clearTimeout(h);
  }, [linkQ]);

  const toggleWorker = (id) => setForm((f) => ({
    ...f,
    assigned_to_ids: f.assigned_to_ids.includes(id) ? f.assigned_to_ids.filter((x) => x !== id) : [...f.assigned_to_ids, id],
  }));
  const setGood = (i, k, v) => setForm((f) => {
    const goods = [...f.goods];
    goods[i] = { ...goods[i], [k]: v };
    return { ...f, goods };
  });

  async function save(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Inserisci le istruzioni dell'attività"); return; }
    setSaving(true);
    const body = {
      ...form, task_type: "activity", linked_task_id: linked?.id || null,
      goods: (form.goods || []).filter((g) => (g.name || "").trim()).map((g) => ({
        ...g, qty: Number(g.qty) || 1,
        colli: g.colli === "" || g.colli == null ? null : Number(g.colli),
      })),
    };
    try {
      if (editing) await api.put(`/installers/tasks/${task.id}`, body);
      else await api.post("/installers/tasks", body);
      toast.success(editing ? "Attività aggiornata" : "Attività creata");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio attività");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/50 backdrop-blur-sm overflow-y-auto py-8 px-4" onClick={onClose} data-testid="activity-modal">
      <div className="bg-cream w-full max-w-xl rounded-sm border border-ink/10 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} className="text-amber-600" />
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-ink">{editing ? "Modifica attività" : "Nuova attività"}</span>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate1 hover:text-ink" data-testid="activity-modal-close"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Istruzioni *</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Caricare la merce per la consegna di domani" className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-title" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Dettagli / note</label>
            <textarea value={form.notes_admin} onChange={(e) => setForm({ ...form, notes_admin: e.target.value })} rows={2} placeholder="Dettagli aggiuntivi per gli operai…" className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-notes" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Data esecuzione *</label>
              <input required type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-date" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Orario</label>
              <input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-time" />
            </div>
          </div>
          <div data-testid="activity-form-enddate-wrap">
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Fine (facoltativa, per attività su più giorni)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input type="date" min={form.scheduled_date || undefined} value={form.scheduled_end_date} onChange={(e) => setForm({ ...form, scheduled_end_date: e.target.value })} className="w-full sm:w-56 border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-enddate" />
              {form.scheduled_end_date && form.scheduled_end_date > form.scheduled_date
                ? <span className="text-[11px] text-sky-800 inline-flex items-center gap-1"><CalendarRange size={12} /> dal {new Date(`${form.scheduled_date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })} al {new Date(`${form.scheduled_end_date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</span>
                : <span className="text-[11px] text-slate1">Lascia vuoto per un solo giorno</span>}
            </div>
          </div>
          {/* Prodotti da prendere a magazzino */}
          <div className="bg-white border border-ink/10 rounded-sm p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-1.5"><Package size={12} /> Prodotti da prendere a magazzino</div>
              <button type="button" onClick={() => setForm((f) => ({ ...f, goods: [...f.goods, { name: "", qty: 1, marca: "", colli: "", riferimento: "", loaded: false, not_found: false }] }))} className="inline-flex items-center gap-1 text-[11px] tracking-wider uppercase text-ink bg-cream2 px-2.5 py-1.5 rounded-sm" data-testid="activity-form-add-good">
                <Plus size={12} /> Aggiungi
              </button>
            </div>
            {form.goods.length === 0 && <div className="text-xs text-slate-400">Nessun prodotto: l&apos;operaio potrà spuntarli come «Caricato» o «Non trovato» dall&apos;app.</div>}
            {form.goods.length > 0 && (
              <div className="hidden md:grid grid-cols-[1fr_110px_52px_56px_auto] gap-2 text-[9px] tracking-[0.15em] uppercase text-slate-400 px-0.5">
                <span>Descrizione</span><span>Marca</span><span>Qtà</span><span>Colli</span><span></span>
              </div>
            )}
            {form.goods.map((g, i) => (
              <div key={g.id || i} className="grid grid-cols-2 md:grid-cols-[1fr_110px_52px_56px_auto] items-center gap-2" data-testid={`activity-form-good-${i}`}>
                <input placeholder="Descrizione prodotto" value={g.name} onChange={(e) => setGood(i, "name", e.target.value)} className="col-span-2 md:col-span-1 border border-ink/15 px-3 py-2 rounded-sm bg-cream text-sm" data-testid={`activity-form-good-name-${i}`} />
                <input placeholder="Marca" value={g.marca || ""} onChange={(e) => setGood(i, "marca", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm" data-testid={`activity-form-good-marca-${i}`} />
                <input type="number" min={1} value={g.qty} onChange={(e) => setGood(i, "qty", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm text-center" title="Quantità" data-testid={`activity-form-good-qty-${i}`} />
                <input type="number" min={0} placeholder="—" value={g.colli ?? ""} onChange={(e) => setGood(i, "colli", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm text-center" title="Numero colli" data-testid={`activity-form-good-colli-${i}`} />
                <div className="flex items-center gap-1.5 justify-end">
                  {g.not_found && <span className="text-[9px] tracking-wider uppercase font-bold text-rose-700 bg-rose-50 border border-rose-300 rounded px-1.5 py-0.5">Non trovato</span>}
                  {g.loaded && <span className="text-[9px] tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Caricato</span>}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, goods: f.goods.filter((_, j) => j !== i) }))} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm" data-testid={`activity-form-good-del-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Operai assegnati</label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {workers.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => toggleWorker(w.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border transition-colors ${form.assigned_to_ids.includes(w.id) ? "bg-ink text-cream border-ink font-bold" : "bg-white text-slate1 border-ink/15 hover:text-ink"}`}
                  data-testid={`activity-form-worker-${w.id}`}
                >
                  <HardHat size={12} /> {w.name}
                </button>
              ))}
              {workers.length === 0 && <span className="text-xs text-slate-400">Nessun operaio registrato</span>}
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Consegna / assistenza collegata (opzionale)</label>
            {linked ? (
              <div className="mt-1.5 flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2.5" data-testid="activity-form-linked">
                {linked.task_type === "assistance" ? <LifeBuoy size={13} className="text-violet-600 shrink-0" /> : <Link2 size={13} className="text-sky-600 shrink-0" />}
                <span className="flex-1 text-sm text-sky-900">
                  <b>{linked.client_name || linked.title}</b>
                  {linked.scheduled_date ? ` · ${fmtDate(linked.scheduled_date)}` : ""}
                  {linked.client_address ? ` · ${linked.client_address}` : ""}
                </span>
                <button type="button" onClick={() => setLinked(null)} className="p-1 text-slate-400 hover:text-ink" data-testid="activity-form-unlink"><X size={14} /></button>
              </div>
            ) : (
              <div className="relative mt-1.5">
                <Search size={13} className="absolute left-3 top-3 text-slate-400" />
                <input value={linkQ} onChange={(e) => setLinkQ(e.target.value)} placeholder="Cerca per cliente, indirizzo, merce…" className="w-full border border-ink/20 pl-8 pr-3 py-2.5 rounded-sm bg-white text-sm" data-testid="activity-form-link-search" />
                {searching && <Loader2 size={13} className="absolute right-3 top-3 animate-spin text-slate-400" />}
                {linkResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink/15 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="activity-form-link-results">
                    {linkResults.map((r) => (
                      <button key={r.id} type="button" onClick={() => { setLinked(r); setLinkQ(""); setLinkResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-cream2 border-b border-ink/5 flex items-center gap-2" data-testid={`activity-form-link-pick-${r.id}`}>
                        {r.task_type === "assistance" ? <LifeBuoy size={12} className="text-violet-600 shrink-0" /> : <Link2 size={12} className="text-sky-600 shrink-0" />}
                        <span className="flex-1 min-w-0 truncate">
                          <b>{r.client_name || r.title}</b>
                          {r.scheduled_date ? ` · ${fmtDate(r.scheduled_date)}` : ""}
                          {r.client_address ? ` · ${r.client_address}` : ""}
                        </span>
                        <span className={`text-[9px] tracking-wider uppercase font-bold shrink-0 ${r.task_type === "assistance" ? "text-violet-700" : "text-slate1"}`}>{r.task_type === "assistance" ? "Assist." : "Consegna"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="text-[10px] text-slate1 mt-1">L&apos;operaio potrà aprire direttamente la consegna collegata (es. per caricare la merce in un giorno diverso da quello di consegna).</div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-ink/10">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase text-slate1 hover:text-ink" data-testid="activity-form-cancel">Annulla</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold disabled:opacity-50" data-testid="activity-form-save">
              {saving && <Loader2 size={13} className="animate-spin" />} {editing ? "Salva modifiche" : "Crea attività"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
